// combat-noise.ts — the loudness table of a hostile action (owner 2026-08-26).
//
// THE authority since R-131 J3 (Janus lane N5e, 2026-10-02): the PHP twin (combat_membership.php FT_COMBAT_NOISE_RADIUS_FT /
// ft_combat_noise_class_from_shape / ft_combat_noise_class / ft_combat_noise_event) is deleted with the server's noise sweep. The GM page's
// noise sweep (websocket/combat-sweep-truth.ts) classifies a committed hostile action here and pulls in whoever HEARS it
// (roll-init/client-combat-membership.ts's noise channel); the action bar / targeting UI can preview the same radius.
//
// Attached as window.ftCombatNoise for template.js and sibling bundles.

export type FtCombatNoiseClass = 'silent' | 'quiet' | 'normal' | 'loud' | 'thunderous';

export const FT_COMBAT_NOISE_RADIUS_FT: Record<FtCombatNoiseClass, number> = {
    silent: 0,
    quiet: 15,
    normal: 30,
    loud: 60,
    thunderous: 120,
};

/** Loudness class → hearing radius in feet (unknown class → normal). */
export function ftCombatNoiseRadiusFt(noiseClass: string): number {
    // own keys only (lane N5f — coordinator Codex round 8 P3: 'toString' used to answer an inherited function)
    return Object.prototype.hasOwnProperty.call(FT_COMBAT_NOISE_RADIUS_FT, noiseClass)
        ? FT_COMBAT_NOISE_RADIUS_FT[noiseClass as FtCombatNoiseClass] : FT_COMBAT_NOISE_RADIUS_FT.normal;
}

export interface FtCombatNoiseShape {
    isSpell: boolean;
    verbal: boolean;
    silenced: boolean;
    hasAoe: boolean;
    boomDamage: boolean; // thunder / fire / force / lightning (the "explosion" proxy)
    unarmed: boolean;
}

/** Pure classifier — identical branch order to ft_combat_noise_class_from_shape(). */
export function ftCombatNoiseClassFromShape(shape: FtCombatNoiseShape): FtCombatNoiseClass {
    if (shape.hasAoe && shape.boomDamage) return 'thunderous';
    if (shape.hasAoe || (shape.verbal && !shape.silenced)) return 'loud';
    if (shape.isSpell && shape.silenced) return 'silent';
    if (shape.unarmed) return 'quiet';
    return 'normal';
}

const BOOM_DAMAGE_VARIABLE_NAMES = new Set(['thunder', 'fire', 'force', 'lightning']);

type ItemLike = Record<string, unknown> & {
    items?: Record<string, unknown>;
    item_layer_types?: Record<string, unknown>;
};

function readLayer(item: ItemLike | null | undefined, key: string): unknown {
    if (!item) return undefined;
    const layers = item.item_layer_types;
    const raw = (layers && typeof layers === 'object' ? (layers as Record<string, unknown>)[key] : undefined) ?? item[key];
    return Array.isArray(raw) ? raw[0] : raw;
}

/**
 * Derive the shape from a compiled ability item_arr + the caster row, then classify. Damage-type
 * checks accept either UUIDs (via the optional catalog lookup) or variable names.
 */
export function ftCombatNoiseClassForAbility(
    ability: ItemLike | null | undefined,
    options: { casterSilenced?: boolean; damageTypeVariableName?: (uuid: string) => string | undefined } = {},
): FtCombatNoiseClass {
    const itemType = String((ability?.items as Record<string, unknown> | undefined)?.item_type ?? ability?.item_type ?? '');
    const isSpell = itemType === 'spell' || itemType === 'curse' || itemType.includes('spell');
    const verbal = isSpell && readLayer(ability, 'spell_component_verbal') != null;
    const hasAoe = readLayer(ability, 'aoe_type') != null;

    const damageTypes: unknown[] = [];
    const layers = ability?.item_layer_types as Record<string, unknown> | undefined;
    const rawDamage = layers?.damage_type ?? ability?.damage_type;
    if (Array.isArray(rawDamage)) damageTypes.push(...rawDamage);
    else if (rawDamage != null) damageTypes.push(rawDamage);
    const boomDamage = damageTypes.some((entry) => {
        const value = typeof entry === 'object' && entry !== null
            ? String((entry as Record<string, unknown>).value ?? (entry as Record<string, unknown>)[0] ?? '')
            : String(entry ?? '');
        const name = options.damageTypeVariableName?.(value) ?? value;
        return BOOM_DAMAGE_VARIABLE_NAMES.has(name.toLowerCase());
    });

    const unarmed = itemType === 'unarmed' || readLayer(ability, 'grapple') != null || readLayer(ability, 'shove') != null;

    return ftCombatNoiseClassFromShape({
        isSpell,
        verbal,
        silenced: options.casterSilenced === true,
        hasAoe,
        boomDamage,
        unarmed,
    });
}

declare global {
    interface Window {
        ftCombatNoise?: {
            radiusFt: typeof ftCombatNoiseRadiusFt;
            classFromShape: typeof ftCombatNoiseClassFromShape;
            classForAbility: typeof ftCombatNoiseClassForAbility;
            table: typeof FT_COMBAT_NOISE_RADIUS_FT;
        };
    }
}

window.ftCombatNoise = {
    radiusFt: ftCombatNoiseRadiusFt,
    classFromShape: ftCombatNoiseClassFromShape,
    classForAbility: ftCombatNoiseClassForAbility,
    table: FT_COMBAT_NOISE_RADIUS_FT,
};
